import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { PitchMix } from "./Research";
import { Search, X, ClipboardList } from "lucide-react";

function PitcherPlan({ pid, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["pitcherPlan", pid],
    queryFn: () => api.get(`/pitcher-plan/${pid}`).then((r) => r.data),
    staleTime: 30 * 60 * 1000,
  });
  if (isLoading) {
    return <div className="card-gold border border-[#a855f7]/30 rounded-xl p-5 text-neutral-500 font-data text-sm animate-pulse" data-testid="pitcher-plan-loading">Building the game plan — pulling the arsenal and lineup matchups (first load can take ~15s)...</div>;
  }
  if (!data?.found) {
    return <div className="card-surface rounded-xl p-5 text-sm text-neutral-500" data-testid="pitcher-plan-empty">No game plan available — this arm is not a probable starter tonight.</div>;
  }
  return (
    <div className="card-gold border border-[#a855f7]/40 rounded-xl p-4 md:p-5 gold-glow" data-testid="pitcher-plan">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-white/10 pb-3">
        <div>
          <h2 className="font-display text-xl md:text-2xl font-black uppercase">{data.name} <span className="text-neutral-500 text-sm font-normal">({data.team})</span> <span className="gold-text">Game Plan</span></h2>
          <div className="text-xs text-neutral-500 mt-0.5">vs {data.opponent} · ERA {data.era ?? "—"} · HR/9 {data.hr9 ?? "—"} · Vulnerability <b className={data.vulnerability >= 7 ? "text-red-400" : data.vulnerability >= 4.5 ? "text-[#f59e0b]" : "text-[#22c55e]"}>{data.vulnerability ?? "—"}/10</b></div>
        </div>
        <button onClick={onClose} data-testid="pitcher-plan-close" className="text-neutral-500 hover:text-white p-1 transition-colors duration-200"><X size={18} /></button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <PitchMix pa={{ pitcher: data.name, mix: data.mix, source: data.source, batter_vs: [] }} />
        <div className="card-surface rounded-lg p-3.5 sm:p-5" data-testid="plan-lineup">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">{data.opponent} Lineup vs This Arsenal</h3>
            <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${data.lineup_status === "confirmed" ? "border-[#22c55e]/50 text-[#22c55e] bg-[#22c55e]/10" : "border-white/20 text-neutral-400"}`} data-testid="plan-lineup-status">
              {data.lineup_status === "confirmed" ? "Confirmed" : "Projected"}
            </span>
          </div>
          <div className="space-y-1.5">
            {data.slots.map((s) => (
              <div key={s.order} className={`rounded-lg border p-2.5 ${s.weak_spot ? "border-[#a855f7]/40 bg-[#a855f7]/5" : "border-white/10"}`} data-testid={`plan-slot-${s.order}`}>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-data text-xs text-neutral-500 w-4">{s.order}</span>
                  <Link to={`/research/${s.mlb_id}`} className={`flex-1 truncate font-semibold hover:text-[#a855f7] transition-colors duration-200 ${s.weak_spot ? "text-[#a855f7]" : "text-neutral-200"}`}>{s.batter}</Link>
                  <span className="font-data text-xs text-neutral-500">{s.ops ?? "—"} OPS</span>
                  <span className={`font-data text-xs font-bold w-10 text-right ${s.weak_spot ? "text-[#22c55e]" : "text-neutral-400"}`}>{s.threat ?? "—"}</span>
                </div>
                {s.vs_pitch?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5 ml-6">
                    {s.vs_pitch.map((v) => (
                      <span key={v.code} title={`${v.pa} PA · BA ${v.ba} · K ${v.k_pct}%`}
                        className={`text-[10px] font-data px-1.5 py-0.5 rounded border ${v.crushes ? "border-[#22c55e]/50 text-[#22c55e] bg-[#22c55e]/10" : "border-white/10 text-neutral-400"}`}>
                        {v.pitch}: {Number(v.slg).toFixed(3)} SLG · {v.pa} PA · {v.hr} HR
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {data.slots.length === 0 && <p className="text-xs text-neutral-600 p-2">Opponent lineup data not available yet.</p>}
          </div>
          {data.locked && <p className="text-xs text-[#a855f7] mt-2">Free preview shows 2 bats — go Pro for the full lineup breakdown.</p>}
        </div>
      </div>
      <p className="text-[10px] text-neutral-600 mt-3">{data.source === "savant" ? "Real Statcast arsenal + each batter's real numbers against those exact pitch types (SLG, PA, HRs)." : "Model-estimated mix — Statcast arsenal not available for this arm yet."} Green chips mean the batter crushes a pitch this guy actually throws.</p>
    </div>
  );
}

function VulnBar({ v }) {
  if (v == null) return <span className="text-neutral-600 text-xs">No sample</span>;
  const color = v >= 7 ? "#ef4444" : v >= 4.5 ? "#f59e0b" : "#22c55e";
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${v * 10}%`, background: color }} />
      </div>
      <span className="font-data text-xs font-bold" style={{ color }}>{v}</span>
    </div>
  );
}

const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function Pitchers() {
  const [q, setQ] = useState("");
  const [planPid, setPlanPid] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["pitchers"], queryFn: () => api.get("/pitchers").then((r) => r.data) });
  const { data: allData } = useQuery({ queryKey: ["allPitchers"], queryFn: () => api.get("/pitchers/all").then((r) => r.data) });
  const rows = data?.pitchers || [];
  const matches = q.trim().length >= 2
    ? (allData?.pitchers || []).filter((p) => norm(`${p.name} ${p.team}`).includes(norm(q.trim()))).slice(0, 12)
    : [];

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Pitcher <span className="gold-text">Analysis</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Search any MLB pitcher's full profile, or scout tonight's probables ranked by vulnerability below.</p>
      </div>

      <div className="card-gold border border-[#a855f7]/30 rounded-xl p-4 md:p-5 fade-up-1 gold-glow" data-testid="pitcher-search-section">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a855f7]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any pitcher... e.g. Skenes, Cole, Wheeler" data-testid="pitcher-search-input"
            className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded-lg pl-10 pr-3 py-3 text-sm outline-none transition-colors duration-200" />
        </div>
        {q.trim().length >= 2 && (
          <div className="mt-3 space-y-2" data-testid="pitcher-search-results">
            {matches.map((p) => (
              <div key={p.id} className="bg-black/40 border border-white/10 rounded-lg p-3" data-testid={`pitcher-result-${p.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <img src={`https://img.mlbstatic.com/mlb-photos/image/upload/w_120,q_100/v1/people/${p.id}/headshot/67/current`} alt="" className="w-10 h-10 rounded-full object-cover bg-white/5" loading="lazy" />
                    <div>
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-[11px] text-neutral-500">{p.team} · {p.ip} IP · {p.wins}-{p.losses} · {p.season}</div>
                    </div>
                  </div>
                  <div className="font-data text-sm"><span className="text-neutral-500 text-xs">ERA</span> <b>{p.era}</b> <span className="text-neutral-500 text-xs ml-2">WHIP</span> <b>{p.whip}</b></div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3 text-center">
                  {[["SwStr%", p.swstr_pct], ["Barrel%", p.barrel_pct], ["FB%", p.fb_pct], ["K%", p.k_pct], ["BB%", p.bb_pct], ["HR/9", p.hr9]].map(([label, val]) => (
                    <div key={label} className="bg-[#0a0a0a] border border-white/10 rounded p-2">
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
                      <div className={`font-data text-sm font-bold mt-0.5 ${label === "HR/9" && val >= 1.4 ? "text-[#22c55e]" : "text-white"}`}>{val ?? "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {matches.length === 0 && <p className="text-xs text-neutral-500" data-testid="pitcher-search-empty">No pitcher matches "{q}" — check the spelling.</p>}
          </div>
        )}
      </div>

      <div className="fade-up-1">
        <h2 className="font-display text-xl font-bold uppercase mb-2">Tonight's <span className="text-[#a855f7]">Probables</span> <span className="text-neutral-500 text-xs font-normal normal-case">ranked by vulnerability — tap Game Plan for the pitch mix + lineup attack</span></h2>
        {planPid && <div className="mb-4"><PitcherPlan pid={planPid} onClose={() => setPlanPid(null)} /></div>}
        {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Pulling tonight's probables...</div>}
        <div className="card-surface rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[950px]">
            <thead>
              <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                <th className="p-3">Pitcher</th><th className="p-3">Matchup</th><th className="p-3 text-center">ERA</th>
                <th className="p-3 text-center">WHIP</th><th className="p-3 text-center">HR/9</th><th className="p-3 text-center">K/9</th>
                <th className="p-3 text-center">HR Allowed</th><th className="p-3">Vulnerability</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={`${p.id}-${p.game}`} className="border-b border-white/5 hover:bg-[#1a1a1a] transition-colors duration-200 cursor-pointer" data-testid={`pitcher-row-${p.id}`}
                  onClick={() => { setPlanPid(p.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                  <td className="p-3">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-[11px] text-neutral-600">{p.team}</div>
                  </td>
                  <td className="p-3 text-neutral-400 text-xs">vs {p.opponent}</td>
                  <td className={`p-3 text-center font-data ${p.era >= 4.5 ? "text-red-400" : "text-neutral-300"}`}>{p.era ?? "—"}</td>
                  <td className="p-3 text-center font-data">{p.whip ?? "—"}</td>
                  <td className={`p-3 text-center font-data font-bold ${p.hr9 >= 1.4 ? "text-[#22c55e]" : "text-neutral-300"}`}>{p.hr9 ?? "—"}</td>
                  <td className="p-3 text-center font-data">{p.k9 ?? "—"}</td>
                  <td className="p-3 text-center font-data">{p.hr_allowed}</td>
                  <td className="p-3"><VulnBar v={p.vulnerability} /></td>
                  <td className="p-3">
                    <button onClick={(e) => { e.stopPropagation(); setPlanPid(p.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`pitcher-plan-btn-${p.id}`}
                      className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#a855f7] border border-[#a855f7]/40 hover:bg-[#a855f7]/10 px-2.5 py-1.5 rounded transition-colors duration-200 whitespace-nowrap">
                      <ClipboardList size={12} /> Game Plan
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {data?.locked && <UpgradeGate feature={`Full pitcher analysis (${data.total} probables tonight)`} />}
      <p className="text-[11px] text-neutral-600">Season stats via the official MLB Stats API. SwStr%, Barrel% and FB% are model estimates derived from K-rate and HR-rate profiles. Green HR/9 means the ball leaves the yard on this guy.</p>
    </div>
  );
}
